/**
 * The sequence view's data, computed locally from a NanomeModel. Ports of the WebGL client:
 *  - Nanome.Adapter/Workspaces/SequenceMenuViewModel.cs: full sequence (GetFullSequenceResidues: present residues
 *    plus missing ones, sorted by chain name then serial), residue views (BuildResidueViews: code, category, chain
 *    start, serial label), atoms -> residue indices (UpdateSequenceSelectedAtoms) and back (SequenceSelectionToSelection)
 *  - Nanome.Domain/Sequences/ResidueCodes.cs: one-letter codes, protein and nucleic tables (the menu itself only uses
 *    the protein table and so shows nucleotides as "DA"; Lite uses both tables so 1BNA reads CGCGAATTCGCG)
 *  - Nanome.Application/Selections/SelectionFilterBuilder.cs (useCurrentModel, highestAltLoc): atoms -> filters for
 *    CreateComponentFromSequence / UpdateComponentFromSequence (SequencesMenuController.cs:35-62)
 * The state shapes match the `sequences` / `sequence.selection` events of WebGlAPI.Sequences.cs.
 */
import type { NanomeModel } from '@nanome-lite/data';
import type { FilterModel, RangeSet } from './dto';

export type SequenceCategory = 'standard' | 'nonStandard' | 'water' | 'ligand' | 'missing';

/** WebGlAPI.Sequences.cs SequenceResidueState, plus the model residue it stands for. */
export interface SequenceResidue {
  index: number;
  code: string;
  name: string;
  chain: string;
  serial: number;
  insertionCode: string;
  missing: boolean;
  category: SequenceCategory;
  chainStart: boolean;
  serialLabel: string | null;
  /** Model residue index (the first one when a file repeats a chain/serial/insertion key); -1 when missing. */
  residueIndex: number;
}

export interface SequenceEntry {
  entrySerial: number;
  modelSerial: number;
  entryName: string;
  residues: SequenceResidue[];
  /** model residue index -> sequence index */
  indexOfResidue: Int32Array;
}

export interface SequenceSlice { selected: number[]; partial: number[] }

// ---- ResidueCodes.cs ---------------------------------------------------------------------------------------------

export const PROTEIN_CODES: Readonly<Record<string, string>> = {
  ALA: 'A', ARG: 'R', ASN: 'N', ASP: 'D', CYS: 'C', GLN: 'Q', GLU: 'E', GLY: 'G', HIS: 'H', HIE: 'H', HID: 'H', HIP: 'H',
  ILE: 'I', LEU: 'L', LYS: 'K', MET: 'M', PHE: 'F', PRO: 'P', SER: 'S', THR: 'T', TRP: 'W', TYR: 'Y', VAL: 'V',
  ASX: 'B', GLX: 'Z', SEC: 'U', PYL: 'O', UNK: 'X',
};

export const NUCLEIC_CODES: Readonly<Record<string, string>> = {
  DA: 'A', DC: 'C', DG: 'G', DT: 'T', DU: 'U', DI: 'I', A: 'A', C: 'C', G: 'G', U: 'U', T: 'T', I: 'I',
  RA: 'A', RC: 'C', RG: 'G', RU: 'U', ADE: 'A', CYT: 'C', GUA: 'G', THY: 'T', URA: 'U', N: 'X',
};

/** ResidueCodes.TryGetOneLetter: protein, then nucleic, then nucleic without an Amber/CHARMM 3/5 terminus marker. */
export function oneLetterCode(name: string): string | null {
  const hit = PROTEIN_CODES[name] ?? NUCLEIC_CODES[name];
  if (hit) return hit;
  if (name.length > 1 && (name.endsWith('3') || name.endsWith('5'))) return NUCLEIC_CODES[name.slice(0, -1)] ?? null;
  return null;
}

/** SequenceMenuViewModel.s_waterResidueNames */
export const WATER_NAMES: ReadonlySet<string> = new Set(['HOH', 'H2O', 'WAT', 'TIP', 'SOL', 'DOD', 'D2O', 'EOH', 'MOH']);

/** MissingSequence.MissingResidueName */
const MISSING_NAME = '-';

interface RawResidue { chain: string; name: string; serial: number; insCode: string; missing: boolean; residueIndex: number }

/** SequenceMenuViewModel.DisplayCode */
function displayCode(r: RawResidue): string {
  if (WATER_NAMES.has(r.name)) return 'O';
  if (r.missing && r.name === MISSING_NAME) return '_';
  if (!r.name) return 'LIG';
  return oneLetterCode(r.name) ?? r.name;
}

/** SequenceMenuViewModel.ClassifyResidue */
function classify(r: RawResidue): SequenceCategory {
  if (WATER_NAMES.has(r.name)) return 'water';
  if (r.missing) return 'missing';
  if (!r.name) return 'ligand';
  if (oneLetterCode(r.name) === null) return 'nonStandard';
  return 'standard';
}

const residueKey = (chain: string, serial: number, insCode: string) => `${chain}\u0000${serial}\u0000${insCode}`;

/** GetFullSequenceResidues + BuildResidueViews for one entry's current model. */
export function buildSequenceEntry(entry: { serial: number; name: string; currentModelSerial: number }, model: NanomeModel): SequenceEntry {
  const r = model.residues;
  const raw: RawResidue[] = [];
  const firstByKey = new Map<string, number>();
  const keyOfResidue: string[] = new Array(r.count);
  for (let i = 0; i < r.count; i++) {
    const chain = model.chains.name[r.chainIndex[i]!] ?? '';
    const key = residueKey(chain, r.serial[i]!, r.insCode[i] ?? '');
    keyOfResidue[i] = key;
    if (firstByKey.has(key)) continue;
    firstByKey.set(key, i);
    raw.push({ chain, name: r.name[i] ?? '', serial: r.serial[i]!, insCode: r.insCode[i] ?? '', missing: false, residueIndex: i });
  }
  const m = model.missingResidues;
  if (m) {
    const seen = new Set<string>();
    for (let i = 0; i < m.chain.length; i++) {
      const key = residueKey(m.chain[i]!, m.serial[i]!, m.insCode[i] ?? '');
      if (firstByKey.has(key) || seen.has(key)) continue;
      seen.add(key);
      raw.push({ chain: m.chain[i]!, name: m.name[i] ?? '', serial: m.serial[i]!, insCode: m.insCode[i] ?? '', missing: true, residueIndex: -1 });
    }
  }
  // chain name (ordinal), then serial; Array.prototype.sort is stable so insertion codes keep file order
  raw.sort((a, b) => (a.chain < b.chain ? -1 : a.chain > b.chain ? 1 : a.serial - b.serial));

  const residues: SequenceResidue[] = [];
  const seqIndexOfKey = new Map<string, number>();
  let lastChain: string | null = null;
  let lastSerial = Number.MIN_SAFE_INTEGER;
  raw.forEach((x, index) => {
    const chainStart = x.chain !== lastChain;
    const showSerial = chainStart || Math.abs(x.serial) % 10 === 0 || Math.abs(lastSerial - x.serial) >= 10;
    residues.push({
      index, code: displayCode(x), name: x.name, chain: x.chain, serial: x.serial, insertionCode: x.insCode, missing: x.missing,
      category: classify(x), chainStart, serialLabel: showSerial ? String(x.serial) : null, residueIndex: x.residueIndex,
    });
    if (!x.missing) seqIndexOfKey.set(residueKey(x.chain, x.serial, x.insCode), index);
    lastChain = x.chain;
    lastSerial = x.serial;
  });
  const indexOfResidue = new Int32Array(r.count).fill(-1);
  for (let i = 0; i < r.count; i++) indexOfResidue[i] = seqIndexOfKey.get(keyOfResidue[i]!) ?? -1;
  return { entrySerial: entry.serial, modelSerial: entry.currentModelSerial, entryName: entry.name, residues, indexOfResidue };
}

/** One-letter string of a chain's present (non-missing) standard residues, e.g. for tests and copy. */
export function chainSequence(seq: SequenceEntry, chain: string): string {
  return seq.residues.filter(r => r.chain === chain && !r.missing && r.category === 'standard').map(r => r.code).join('');
}

export const sequenceEntryKey = (e: { entrySerial: number; modelSerial: number }) => `${e.entrySerial}:${e.modelSerial}`;

/** Model residue indices behind each sequence index (usually one). */
function residuesBySequenceIndex(seq: SequenceEntry): number[][] {
  const out: number[][] = seq.residues.map(() => []);
  for (let i = 0; i < seq.indexOfResidue.length; i++) {
    const s = seq.indexOfResidue[i]!;
    if (s >= 0) out[s]!.push(i);
  }
  return out;
}

/** UpdateSequenceSelectedAtoms: residues whose every atom is selected are `selected`, the rest with some atoms `partial`. */
export function sequenceSelectionFromAtoms(seq: SequenceEntry, model: NanomeModel, atoms: ArrayLike<number>): SequenceSlice {
  const count = new Map<number, number>();
  for (let i = 0; i < atoms.length; i++) {
    const s = seq.indexOfResidue[model.residueIndex[atoms[i]!] ?? -1] ?? -1;
    if (s >= 0) count.set(s, (count.get(s) ?? 0) + 1);
  }
  if (!count.size) return { selected: [], partial: [] };
  const total = new Map<number, number>();
  for (let i = 0; i < seq.indexOfResidue.length; i++) {
    const s = seq.indexOfResidue[i]!;
    if (count.has(s)) total.set(s, (total.get(s) ?? 0) + model.residues.atomCount[i]!);
  }
  const selected: number[] = [], partial: number[] = [];
  for (const [s, n] of [...count].sort((a, b) => a[0] - b[0])) (n === total.get(s) ? selected : partial).push(s);
  return { selected, partial };
}

/**
 * SequenceSelectionToSelection: selected residues contribute all their atoms; partial residues keep only the atoms of
 * `current` (the live selection) inside them. Returns sorted model atom indices.
 */
export function atomsFromSequenceSelection(seq: SequenceEntry, model: NanomeModel, selected: Iterable<number>, partial: Iterable<number>, current: ArrayLike<number> = []): number[] {
  const byIndex = residuesBySequenceIndex(seq);
  const out = new Set<number>();
  for (const s of selected) {
    for (const r of byIndex[s] ?? []) {
      const start = model.residues.atomStart[r]!, n = model.residues.atomCount[r]!;
      for (let a = start; a < start + n; a++) out.add(a);
    }
  }
  const partialSet = new Set(partial);
  if (partialSet.size) {
    for (let i = 0; i < current.length; i++) {
      const a = current[i]!;
      const s = seq.indexOfResidue[model.residueIndex[a] ?? -1] ?? -1;
      if (partialSet.has(s)) out.add(a);
    }
  }
  return [...out].sort((a, b) => a - b);
}

// ---- SelectionFilterBuilder.cs ------------------------------------------------------------------------------------

/** RangeSetModel.FromSerials: runs of consecutive serials become ranges, singles go to sets. */
export function rangeSet(serials: Iterable<number>): RangeSet {
  const values = [...new Set(serials)].sort((a, b) => a - b);
  const ranges: [number, number][] = [];
  const sets: number[] = [];
  for (let i = 0; i < values.length;) {
    const start = values[i]!;
    let end = start;
    i++;
    while (i < values.length && values[i] === end + 1) { end = values[i]!; i++; }
    if (start === end) sets.push(start); else ranges.push([start, end]);
  }
  return { ranges, sets };
}

const HIGHEST_ALT_LOC: FilterModel = { kind: 'AltLocs', occupancySelection: 'Highest' };

/**
 * The filter groups for one entry's atoms, each a pipeline: the whole entry, whole chains, full residues of a chain,
 * then the remaining atoms (EnumeratedAtoms). Root = [EntrySerial, CurrentModel] (useCurrentModel), every group ends
 * with AltLocs Highest (highestAltLoc). Residues use ResidueSerials when the chain has no insertion codes (readable
 * ranges), else Residues.
 */
export function selectionFilterGroups(entrySerial: number, model: NanomeModel, atoms: ArrayLike<number>): FilterModel[][] {
  const selected = new Set<number>();
  for (let i = 0; i < atoms.length; i++) if (atoms[i]! < model.atomCount) selected.add(atoms[i]!);
  if (!selected.size) return [];
  const root = (): FilterModel[] => [{ kind: 'EntrySerial', entrySerial }, { kind: 'CurrentModel' }];
  if (selected.size === model.atomCount) return [[...root(), { ...HIGHEST_ALT_LOC }]];

  const r = model.residues;
  const groups: FilterModel[][] = [];
  const remaining: number[] = [];
  // chain name -> atoms (several chain instances can share a name)
  const byChain = new Map<string, number[]>();
  for (const a of selected) {
    const name = model.chains.name[model.chainIndex[a]!] ?? '';
    let list = byChain.get(name);
    if (!list) byChain.set(name, list = []);
    list.push(a);
  }
  const chainTotal = new Map<string, number>();
  for (let a = 0; a < model.atomCount; a++) {
    const name = model.chains.name[model.chainIndex[a]!] ?? '';
    chainTotal.set(name, (chainTotal.get(name) ?? 0) + 1);
  }
  for (const chain of [...byChain.keys()].sort((x, y) => (x < y ? -1 : x > y ? 1 : 0))) {
    const chainAtoms = byChain.get(chain)!;
    if (chainAtoms.length === chainTotal.get(chain)) {
      groups.push([...root(), { kind: 'ChainNames', names: [chain] }, { ...HIGHEST_ALT_LOC }]);
      continue;
    }
    // residue key -> selected atoms, and the residue's total atoms (a key may span several model residues)
    const byResidue = new Map<string, { serial: number; insCode: string; atoms: number[] }>();
    for (const a of chainAtoms) {
      const ri = model.residueIndex[a]!;
      const key = residueKey(chain, r.serial[ri]!, r.insCode[ri] ?? '');
      let g = byResidue.get(key);
      if (!g) byResidue.set(key, g = { serial: r.serial[ri]!, insCode: r.insCode[ri] ?? '', atoms: [] });
      g.atoms.push(a);
    }
    const residueTotal = new Map<string, number>();
    let chainHasInsertionCodes = false;
    for (let ri = 0; ri < r.count; ri++) {
      if ((model.chains.name[r.chainIndex[ri]!] ?? '') !== chain) continue;
      if (r.insCode[ri]) chainHasInsertionCodes = true;
      const key = residueKey(chain, r.serial[ri]!, r.insCode[ri] ?? '');
      if (byResidue.has(key)) residueTotal.set(key, (residueTotal.get(key) ?? 0) + r.atomCount[ri]!);
    }
    const full: { serial: number; insCode: string }[] = [];
    const ordered = [...byResidue.entries()].sort(([, x], [, y]) => (x.serial - y.serial) || (x.insCode < y.insCode ? -1 : x.insCode > y.insCode ? 1 : 0));
    for (const [key, g] of ordered) {
      if (g.atoms.length === residueTotal.get(key)) full.push({ serial: g.serial, insCode: g.insCode });
      else remaining.push(...g.atoms);
    }
    if (!full.length) continue;
    const residueFilter: FilterModel = chainHasInsertionCodes
      ? { kind: 'Residues', residues: full.map(f => (f.insCode ? { residueSerial: f.serial, insertionCode: f.insCode.charCodeAt(0) } : { residueSerial: f.serial })) }
      : { kind: 'ResidueSerials', serialRangeSet: rangeSet(full.map(f => f.serial)) };
    groups.push([...root(), { kind: 'ChainNames', names: [chain] }, residueFilter, { ...HIGHEST_ALT_LOC }]);
  }
  if (remaining.length) {
    const serials = remaining.map(a => model.atomSerial[a]!);
    groups.push([...root(), { kind: 'EnumeratedAtoms', serialRangeSet: rangeSet(serials) }, { ...HIGHEST_ALT_LOC }]);
  }
  return groups;
}

/** Filter.UnionFilter appends: the first group is the pipeline, every later group becomes a trailing Union. */
export function unionFilterGroups(groups: FilterModel[][]): FilterModel[] {
  if (!groups.length) return [];
  const [first, ...rest] = groups;
  return [...first!, ...rest.map((g): FilterModel => ({ kind: 'Union', containedFilters: g }))];
}

/** SelectionFilterBuilder.Build over several entries, in entry serial order. */
export function selectionFilters(perEntry: { entrySerial: number; model: NanomeModel; atoms: ArrayLike<number> }[]): FilterModel[] {
  const groups = [...perEntry].sort((a, b) => a.entrySerial - b.entrySerial).flatMap(e => selectionFilterGroups(e.entrySerial, e.model, e.atoms));
  return unionFilterGroups(groups);
}

/** SequenceMenuViewModel.UpdateSequenceSelectedAtoms: "<entry names, 10 chars + '...'>: <component name>". */
export function activeComponentLabel(entryNames: string[], componentName: string): string {
  const names = entryNames.join('-');
  return `${names.length > 10 ? `${names.slice(0, 10)}...` : names}: ${componentName}`;
}
